import {
  Button,
  Chip,
  ChipGroup,
  Icon,
  PlusSVG,
  ErrorFilledSVG,
} from "@newsela/angelou";
import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";
import { IStandard } from "@/search/Langston/types/IStandard";
import {
  wrapperCss,
  fieldsetCss,
  labelContainerCss,
  legendCss,
  standardsButtonCss,
  gradientTextCss,
  chipGroupCss,
  chipCss,
  chipCloseButtonCss,
  errorContainerCss,
  errorIconCss,
} from "./LessonPlannerStandards.style";

const ButtonWithTrackEvent = withTrackEvent(Button);
const ChipWithTrackEvent = withTrackEvent(Chip);

interface LessonPlannerStandardsProps {
  selectedStandards: IStandard[];
  setSelectedStandards: (standards: IStandard[]) => void;
  setStandardsModalActive: (active: boolean) => void;
  error: string;
}

const LessonPlannerStandards = ({
  selectedStandards,
  setSelectedStandards,
  setStandardsModalActive,
  error,
}: LessonPlannerStandardsProps) => {
  const handleRemoveStandard = (_: unknown, value: string) => {
    setSelectedStandards(
      selectedStandards.filter((standard) => standard.id !== value),
    );
  };

  const handleOpenModal = () => {
    setStandardsModalActive(true);
  };

  return (
    <div css={wrapperCss}>
      <fieldset css={fieldsetCss}>
        <div css={labelContainerCss}>
          <legend css={legendCss}>Standards</legend>
          <p>Select up to 3 standards you want to apply to this lesson.</p>
        </div>
        <ButtonWithTrackEvent
          __cssFor={{ root: standardsButtonCss }}
          flavor={Button.flavors.polarSecondary}
          dataProps={{
            "data-qa-selector": "lesson-planner-add-standards-button",
          }}
          icon={{
            SvgComponent: PlusSVG,
            alignment: Button.iconAlignments.right,
          }}
          actionName={actionNames.interact}
          actionPrefix={actionPrefixes.initiate}
          objectType={objectTypes.content}
          streamProperties={{
            description:
              "Open standards modal in lesson planner tool to add standards",
          }}
          onClick={handleOpenModal}
        >
          <span css={gradientTextCss}>Add standards</span>
        </ButtonWithTrackEvent>
      </fieldset>
      {selectedStandards.length > 0 && (
        <ChipGroup
          groupName="lesson-planner-standards"
          chipType={ChipGroup.chipTypes.input}
          size={ChipGroup.chipSizes.small}
          onChipEvent={handleRemoveStandard}
          __cssFor={{ root: chipGroupCss }}
        >
          {selectedStandards.map((standard) => (
            <ChipWithTrackEvent
              key={standard.id}
              __cssFor={{
                closeButtonSvg: chipCloseButtonCss,
                root: chipCss,
              }}
              text={standard.name}
              value={standard.id}
              objectType={objectTypes.content}
              actionName={actionNames.delete}
              streamProperties={{
                description:
                  "Remove selected standard from lesson planner tool",
              }}
            />
          ))}
        </ChipGroup>
      )}
      {error && (
        <div css={errorContainerCss} aria-live="assertive">
          <Icon
            __cssFor={{ root: errorIconCss }}
            SvgComponent={ErrorFilledSVG}
            isDecorative={false}
            title="Error"
          />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default LessonPlannerStandards;
